<template>
    <component
        :is="componentType"
        :on-before="componentType === Link ? handleBeforeVisit : undefined"
        :type="componentType === 'button' ? 'button' : undefined"
        :disabled="isNavigating || undefined"
        @click="handleClick">
        <slot></slot>
    </component>
</template>

<script setup>
import { Link } from "@inertiajs/vue3";
import { isInIFrame, openUrl } from "~vue/utils";
import { logError } from "~vue/utils/logUtils";
import { ref, useAttrs } from "vue";

const BEFORE_NAVIGATION_TIMEOUT_MS = 1500;

const props = defineProps({
    // Async work (e.g. an analytics event) that must finish before a full-page navigation unloads it.
    // Only awaited in an iFrame (MS Teams); an Inertia visit keeps the page alive, so it is not awaited there.
    before: { type: Function, default: undefined },
    onBefore: { type: Function, default: undefined },
});

const attrs = useAttrs();
const isNavigating = ref(false);

// Returns the Inertia Link component unless we're in an iFrame (MS Teams) or the method is not GET
const getComponentType = () => {
    if (!isInIFrame() || (attrs.method && attrs.method.toLowerCase() !== "get")) {
        return Link;
    }
    return "button";
};

const componentType = getComponentType();

const runBefore = () => {
    try {
        Promise.resolve(props.before?.()).catch(logError);
    } catch (error) {
        logError(error);
    }
};
// Inertia prevents the native click before our handler runs; use its visit hook for intercepted clicks.
const handleBeforeVisit = (visit) => {
    if (props.onBefore?.(visit) === false) {
        return false;
    }
    runBefore();
};

const handleClick = async (event) => {
    // A click an ancestor or descendant already prevented (a row renaming its title) must not navigate,
    // which is also how Inertia's Link treats it.
    if (event.defaultPrevented) {
        return;
    }
    if (componentType === Link) {
        runBefore();
        if (!attrs.href) {
            // log an error for missing href prop for now, and use this to fallback to the openUrl util
            // in the future typescript will catch prop related issues
            logError("BaseLink.vue: 'href' attribute is required for Inertia Link.");
            openUrl(attrs.href, { queryParams: attrs.data });
        }
    } else {
        isNavigating.value = true;
        // Best-effort work (analytics) must never strand the user on a stalled or failed request.
        await Promise.race([
            Promise.resolve()
                .then(() => props.before?.())
                .catch(logError),
            new Promise((resolve) => setTimeout(resolve, BEFORE_NAVIGATION_TIMEOUT_MS)),
        ]);
        openUrl(attrs.href, { queryParams: attrs.data });
    }
};
</script>
